import React, { useEffect, useState } from "react";
import { ReactTooltip } from "web/react/components/tooltip/tooltip";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import analytics from "web/script/analytics/analytics";
import InstantNotification from "web/script/components/instant-notification";
import userProfiler from "web/script/modules/userprofiler";
import globalEvents from "web/script/utils/global-events";

interface InstantFeedbackProps extends ReactComponentProps {
    message: string;
    area: "heart" | "saved-search";
    category?: string;
    direction?: "bottom" | "top";
}

function computeCategory(area: string, category: string): string {
    switch (area) {
        case "heart":
            return `${category}_save`;
        case "saved-search":
            return `${category}_DU`;
        default:
            return category;
    }
}

function InstantFeedback({
    children,
    area,
    category = "InstantFdbk",
    message,
    direction,
}: InstantFeedbackProps): React.ReactElement {
    const [autoShow, setAutoShow] = useState<number | undefined>(undefined);
    const [autoHide, setAutoHide] = useState<number | undefined>(undefined);
    const computedCategory = computeCategory(area, category);
    const { isMobileViewport } = useDomViewport();

    function markAsShown(): void {
        if (area === "heart") {
            (userProfiler as any).setHeartNotificationShown();
        } else if (area === "saved-search") {
            (userProfiler as any).setSavedSearchtNotificationShown();
        } else {
            throw Error(
                "Did not receive correct 'area' set when initializing instant-feedback component"
            );
        }
    }

    function showTooltip(): void {
        setAutoShow(0.001);
        setAutoHide(5);
        markAsShown();
    }

    function showNotification(): void {
        // analytics event is triggered within the InstantNotification component
        const instantNotification = new (InstantNotification as any)({
            icon: area,
            message: message,
            category: computedCategory,
        });
        instantNotification.appendTo(document.body);
        markAsShown();
    }

    function show(event: any, eventArea: string): void {
        if (eventArea !== area || !userProfiler.isLoggedIn()) {
            return;
        }

        if (
            (area === "heart" && (userProfiler as any).getHeartNotificationShown()) ||
            (area === "saved-search" && (userProfiler as any).getSavedSearchtNotificationShown())
        ) {
            return;
        }

        if (isMobileViewport) {
            showTooltip();
        } else {
            showNotification();
        }
    }

    useEffect(() => {
        globalEvents.on("instant-feedback-show", show);

        return () => globalEvents.off("instant-feedback-show", show);
    }, []);

    function onTooltipShow(): void {
        analytics.event(computedCategory, "shown", undefined, true);
    }

    function onTooltipHide(): void {
        analytics.event(computedCategory, "AutoHide", undefined, true);
    }

    function onTooltipClose(): void {
        analytics.event(computedCategory, "Clicked", "Close");
    }

    return isMobileViewport ? (
        <ReactTooltip
            manual
            withCloseButton
            theme="light"
            content={message}
            direction={direction}
            autoHide={autoHide}
            autoShow={autoShow}
            onShow={onTooltipShow}
            onHide={onTooltipHide}
            onClose={onTooltipClose}
        >
            {children}
        </ReactTooltip>
    ) : (
        <>{children}</>
    );
}

export default InstantFeedback;
